import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { Close, Delete, PlayArrow } from '@mui/icons-material';
import { Camera } from '../types';
import { getRecordings, getClipUrl, deleteRecording, RecordingSegment } from '../frigateApi';
import { useLanguage } from '../i18n/LanguageContext';

interface Props {
  camera: Camera | null;
  gatewayUrl: string;
  open: boolean;
  onClose: () => void;
  onPlay: (url: string) => void;
}

export const RecordingsDialog: React.FC<Props> = ({ camera, gatewayUrl, open, onClose, onPlay }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [segments, setSegments] = useState<RecordingSegment[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<RecordingSegment | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open || !camera || !gatewayUrl) return;
    setLoading(true);
    setSegments([]);
    getRecordings(gatewayUrl, camera.name)
      .then(data => setSegments([...data].reverse()))
      .catch(() => setSegments([]))
      .finally(() => setLoading(false));
  }, [open, camera, gatewayUrl]);

  const fmtTime = (ts: number) => new Date(ts * 1000).toLocaleString();

  const fmtDuration = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = Math.round(secs % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const id = confirmDelete.id;
    setDeleting(true);
    try {
      await deleteRecording(gatewayUrl, id);
      setSegments(prev => prev.filter(s => s.id !== id));
      setConfirmDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
        <span>{t.cameraRecordings}{camera ? ` — ${camera.name}` : ''}</span>
        <IconButton size="small" aria-label={t.a11yClose} onClick={onClose}><Close fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, minHeight: 120 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : segments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary" variant="body2">{t.cameraRecordingsNone}</Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {segments.map(seg => (
              <ListItem
                key={seg.id}
                divider
                secondaryAction={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      aria-label={`${t.a11yPlayRecording}: ${fmtTime(seg.start_time)}`}
                      onClick={() => {
                        onPlay(getClipUrl(gatewayUrl, seg.id));
                        onClose();
                      }}
                    >
                      <PlayArrow fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={`${t.a11yDeleteRecording}: ${fmtTime(seg.start_time)}`}
                      onClick={() => setConfirmDelete(seg)}
                      sx={{ color: 'error.light' }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemText
                  primary={fmtTime(seg.start_time)}
                  secondary={fmtDuration(seg.duration)}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <Dialog open={confirmDelete !== null} onClose={() => !deleting && setConfirmDelete(null)} maxWidth="xs">
        <DialogTitle sx={{ py: 1.5 }}>{t.cameraDeleteButton}</DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2">
            {t.cameraDeleteConfirm}
            {confirmDelete ? ` (${fmtTime(confirmDelete.start_time)})` : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleting}>
            {t.nodeEditCancel}
          </Button>
          <Button onClick={handleDelete} color="error" disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : t.cameraDeleteButton}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
